import { CategoryPreferences } from '@segment/consent-manager/types/types';

import { loadUserPreferences } from '@pflegia/analytics/ui/consent/load-user-preferences';
import { FBCLID } from '@pflegia/types/common/constants/analytics';
import { ANALYTICS_NOT_AVAILABLE } from '@pflegia/ui/application/analytics/constants/errors';
import { AnalyticsService } from '@pflegia/ui/application/interfaces/analytics-service.interface';
import { MethodExecutionGuard } from '@pflegia/ui/application/utils/decorators/method-execution-guard.decorator';
import { cookieStorage } from '@pflegia/ui/storage/cookie-storage';

import { ADS_CONSENT_STORAGE_KEYS, ANALYTICS_CONSENT_STORAGE_KEYS, LOGGED_IN_CANDIDATE } from './constants';
import { Ga4Session, getGa4Session } from './ga4/get-ga4-session';
import { PageView, QuestionCategoryEventProperties } from './types';

const MAX_PENDING_EVENTS = 100;

interface SegmentOptions {
  ga4MeasurementId?: string;
  getPageProperties?: (pathname: string) => Record<string, unknown>;
}

class SegmentServiceBase implements AnalyticsService {
  private customPreferences?: CategoryPreferences;
  private destinationPreferences?: CategoryPreferences;
  private pageUrl?: string;
  private pageReferrer?: string;
  private pendingDispatch?: Promise<void>;
  private pendingEvents?: VoidFunction[] = [];

  constructor(
    private readonly integrations: Record<string, unknown>,
    private readonly options: SegmentOptions = {},
  ) {
    this.loadUserPreferences();
  }
  async loadUserPreferences() {
    const segment = await loadUserPreferences();
    this.customPreferences = segment.customPreferences;
    this.destinationPreferences = segment.destinationPreferences;
  }

  userHasGrantedConsent() {
    return this.customPreferences?.marketingAndAnalytics && this.customPreferences?.advertising;
  }

  getAdsStorageConsent() {
    return ADS_CONSENT_STORAGE_KEYS.reduce(
      (acc, key) => ({ ...acc, [key]: this.customPreferences?.advertising ? 'granted' : 'denied' }),
      {},
    );
  }
  getAnalyticsStorageConsent() {
    return ANALYTICS_CONSENT_STORAGE_KEYS.reduce(
      (acc, key) => ({ ...acc, [key]: this.customPreferences?.marketingAndAnalytics ? 'granted' : 'denied' }),
      {},
    );
  }

  getID(): string | undefined {
    if (typeof window.analytics.user !== 'function') return undefined;
    return window.analytics.user().id() ?? window.analytics.user().anonymousId();
  }
  identify(userId: string): void {
    window.analytics.identify(userId, { type: LOGGED_IN_CANDIDATE }, { integrations: this.integrations });
    this.loadUserPreferences();
  }
  trackEvent({ eventName, data, withIntegrations }: { eventName: string; data?: Record<string, unknown>; withIntegrations?: boolean }) {
    const fbclid = cookieStorage.getItem(FBCLID);
    const context = this.getPageContext();
    const properties = { ...context, ...data, fbclid };
    const timestamp = new Date();
    this.dispatch((session, isDeferred) =>
      window.analytics.track(
        eventName,
        {
          ...properties,
          ...session,
          ...this.getAnalyticsStorageConsent(),
        },
        {
          context: this.getSegmentContext(context),
          ...(withIntegrations && { integrations: this.integrations }),
          ...(isDeferred && { timestamp }),
        },
      ),
    );
  }

  private getSegmentContext(properties: Record<string, unknown>) {
    const { url, path, search, title, referrer } = properties;
    return { page: { url, path, search, title, referrer } };
  }

  private getPageContext(url = window.location.href, isPageView = false) {
    const location = new URL(url, window.location.href);
    const referrer = location.href === this.pageUrl ? (this.pageReferrer ?? document.referrer) : (this.pageUrl ?? document.referrer);
    if (isPageView) {
      this.pageUrl = location.href;
      this.pageReferrer = referrer;
    }
    return {
      url: location.href,
      path: location.pathname,
      search: location.search,
      title: document.title,
      referrer,
      // Explicit GA4 parameters can be mapped by the Segment destination without changing other destinations.
      // eslint-disable-next-line eslint-compat/camelcase
      page_location: location.href,
      // eslint-disable-next-line eslint-compat/camelcase
      page_title: document.title,
      // eslint-disable-next-line eslint-compat/camelcase
      page_referrer: referrer,
      ...this.options.getPageProperties?.(location.pathname),
    };
  }

  /** Holds events until releasePendingEvents is called, after that sends them right away. */
  private dispatch(send: (session: Partial<Ga4Session>, isDeferred: boolean) => void) {
    if (!this.pendingEvents) {
      this.dispatchWithGa4Session(session => send(session, false));
      return;
    }
    if (this.pendingEvents.length < MAX_PENDING_EVENTS) {
      this.pendingEvents.push(() => this.dispatchWithGa4Session(session => send(session, true)));
    }
  }

  private dispatchWithGa4Session(send: (session: Partial<Ga4Session>) => void) {
    const session =
      this.options.ga4MeasurementId && this.customPreferences?.marketingAndAnalytics
        ? getGa4Session(this.options.ga4MeasurementId)
        : undefined;
    if (!session && !this.pendingDispatch) {
      send({});
      return;
    }
    // Start the reads at capture time, but preserve page/event order even if Google answers out of order.
    const pending = Promise.all([this.pendingDispatch, session])
      .then(async ([, identifiers]) => {
        if (!identifiers?.client_id) {
          send({});
          return;
        }
        // Read the existing preference without changing consent state or the legacy preference cache.
        const latestPreferences = await loadUserPreferences().catch(() => undefined);
        send(latestPreferences?.customPreferences?.marketingAndAnalytics ? identifiers : {});
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error('Failed to dispatch Segment event', error);
      });
    this.pendingDispatch = pending;
    pending.then(() => {
      if (this.pendingDispatch === pending) this.pendingDispatch = undefined;
    });
  }

  setAnonymousId(id: string) {
    this.loadUserPreferences();
    if (this.core()) {
      this.core().setAnonymousId(id);
    }
  }

  trackQuestionAnswers(eventName: string, { question: { category, id, transformer }, ...rest }: QuestionCategoryEventProperties) {
    /* eslint-disable eslint-compat/camelcase */
    const combinedEventProperties = {
      ...rest,
      ...(category && { question_category: transformer?.(category) ?? category }),
      ...(id && { question_id: id }),
    };
    /* eslint-enable eslint-compat/camelcase */

    return this.trackEvent({ eventName, data: combinedEventProperties });
  }

  ready(callBack: VoidFunction) {
    this.loadUserPreferences();
    window.analytics.ready(callBack);
  }

  /**
   * Track a page view (based on SegmentAnalytics.page())
   * @param url current page url
   * @param properties additional properties for the event
   */
  page(url?: string, properties?: Record<string, unknown>) {
    const pageView = this.capturePageView(url, properties);
    this.dispatch((session, isDeferred) => this.sendPageView(pageView, session, isDeferred));
  }

  /** Reads the page context now, so a page view sent later still describes the page it was called on. */
  capturePageView(url?: string, properties?: Record<string, unknown>): PageView {
    const fbclid = cookieStorage.getItem(FBCLID);
    const context = this.getPageContext(typeof properties?.url === 'string' ? properties.url : url, true);
    const { hash } = new URL(context.url);
    const hashParams = hash ? Object.fromEntries(new URLSearchParams(hash.substring(1))) : {};
    return {
      name: url,
      properties: {
        ...context,
        ...properties,
        ...hashParams,
        ...(!properties?.search && hash && { search: hash.replace('#', '?') }),
        ...(fbclid && { fbclid }),
      },
      timestamp: new Date(),
    };
  }

  /** Sends the landing page view first, then everything held since page load, in call order. */
  releasePendingEvents(landingPageView?: PageView) {
    if (!this.pendingEvents) return;
    const { pendingEvents } = this;
    this.pendingEvents = undefined;
    if (landingPageView) this.dispatchWithGa4Session(session => this.sendPageView(landingPageView, session, true));
    pendingEvents.forEach(send => send());
  }

  private sendPageView({ name, properties, timestamp }: PageView, session: Partial<Ga4Session>, isDeferred: boolean) {
    window.analytics.page(
      name,
      {
        ...properties,
        ...session,
        ...this.getAdsStorageConsent(),
        ...this.getAnalyticsStorageConsent(),
      },
      { context: this.getSegmentContext(properties), ...(isDeferred && { timestamp }) },
    );
  }

  /**
   * Clear a segment user identity from local storage.
   */
  resetID() {
    if (typeof window.analytics.user !== 'function') return;
    if (window.analytics.user().id()) {
      window.analytics.reset();
      this.loadUserPreferences();
    }
  }

  core() {
    return window.analytics;
  }
}

export const SegmentService = MethodExecutionGuard({
  checkCondition: () => globalThis.window?.analytics,
  onCheckFail: () => {
    if (!process.env.NEXT_PUBLIC_ANALYTICS_WRITE_KEY) return;
    throw new Error(ANALYTICS_NOT_AVAILABLE);
  },
})(SegmentServiceBase);
